import { Switch } from 'antd';
import { LoadingContainer } from 'app/components/LoadingContainer';
import RenderIf from 'app/components/RenderIf';
import { getDashboardRevenueApi } from 'app/services/homePageApi';
import _ from 'lodash';
import React, { useMemo, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import styled from 'styled-components';
import { buildFullNameMap } from '../constants';
import { useCatalogNameMap } from '../hooks/useCatalogNameMap';
import {
  DASHBOARD_REPORT_TYPE,
  groupRevenueRows,
  useDashboardReport,
} from '../hooks/useDashboardReport';
import ReportEmpty from './ReportEmpty';
import {
  AxisTick,
  calcRevenuePercent,
  ChartScroll,
  formatCurrencyShort,
  ReportCardHeader,
  REPORT_MOBILE_WIDTH,
  ReportTooltip,
  useIsReportMobile,
} from './reportChartUtils';
import ReportPeriodFilter from './ReportPeriodFilter';

const COLORS = [
  'var(--color-9)',
  '#F9B217',
  '#6FD195',
  '#537FF1',
  '#1F94FF',
  '#FF6B6B',
  '#8C63DA',
  '#2BB7DC',
];
const LINE_COLOR = 'var(--color-14)';

// Catalog cần chú thích thêm loại vé khi hover tooltip, vd: Vé cáp treo/Công viên (Vé lẻ)
const CATALOG_TOOLTIP_NOTE_KEYS: Record<string, string[]> = {
  S1: ['b2b.txt.singleTicket', 'b2b.txt.individualTicket'],
};

interface ITicketTypeRevenueReportProps {
  translations: any;
}

export function TicketTypeRevenueReport(props: ITicketTypeRevenueReportProps) {
  const { translations } = props;
  const { getCatalogName, isLoadingCatalog } = useCatalogNameMap();
  const {
    rows,
    period,
    onChangePeriod,
    isLoading: isLoadingRevenue,
  } = useDashboardReport({
    actionApi: getDashboardRevenueApi,
    type: DASHBOARD_REPORT_TYPE.CATALOG,
  });
  const [isPieChart, setIsPieChart] = useState(false);

  const data = useMemo(
    () =>
      groupRevenueRows(
        rows,
        row => String(row?.catalog ?? ''),
        code => {
          const name = getCatalogName(code);
          const noteKeys =
            CATALOG_TOOLTIP_NOTE_KEYS[String(code).trim().toUpperCase()] ?? [];
          const noteText =
            noteKeys.map(key => translations?.[key]).find(Boolean) ?? '';
          return {
            name,
            fullName: noteText ? `${name} (${noteText})` : name,
          };
        },
      ),
    [rows, getCatalogName, translations],
  );
  const fullNameByShortName = useMemo(() => buildFullNameMap(data), [data]);
  const isLoading = isLoadingRevenue || isLoadingCatalog;
  const totalRevenue = useMemo(() => _.sumBy(data, 'revenue'), [data]);
  const isMobile = useIsReportMobile();
  const chartHeight = isMobile ? 300 : 360;

  return (
    <div className="box-report-card">
      <ReportCardHeader>
        <div className="report-card-title">
          <div className="sub-title">
            {translations?.['b2b.txt.revenueByTicketCategory'] ?? ''}
          </div>
          <div className="report-card-note">
            {translations?.['b2b.txt.usedTicketNote'] ?? ''}
          </div>
        </div>
        <div className="report-card-actions">
          <ReportPeriodFilter
            translations={translations}
            period={period}
            onChange={onChangePeriod}
          />
          <ChartTypeSwitch checked={isPieChart} onChange={setIsPieChart} />
        </div>
      </ReportCardHeader>
      <LoadingContainer loading={isLoading} minHeight={`${chartHeight}px`}>
        <RenderIf
          condition={!_.isEmpty(data)}
          fallback={
            <ReportEmpty translations={translations} height={chartHeight} />
          }
        >
          {isPieChart ? (
            <PieSection>
              <div className="pie-chart-area">
                <ResponsiveContainer width="100%" height={isMobile ? 260 : 320}>
                  <PieChart>
                    <Tooltip
                      content={
                        <ReportTooltip
                          translations={translations}
                          totalRevenue={totalRevenue}
                        />
                      }
                    />
                    <Pie
                      data={data}
                      cx="50%"
                      cy="50%"
                      outerRadius={isMobile ? 95 : 120}
                      dataKey="revenue"
                      nameKey="name"
                    >
                      {data.map((item, index) => (
                        <Cell
                          key={`cell-${item.name}`}
                          fill={COLORS[index % COLORS.length]}
                          stroke={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="pie-caption">
                  {translations?.['b2b.txt.productCategoryShare'] ?? ''}
                </div>
              </div>
              <PieLegend data={data} />
            </PieSection>
          ) : (
            <ChartScroll $itemCount={data.length}>
              <div className="chart-scroll-inner">
                <ResponsiveContainer width="100%" height={chartHeight}>
                  <ComposedChart
                    data={data}
                    margin={{ top: 8, right: 8, bottom: 8, left: 0 }}
                  >
                    <defs>
                      <filter
                        id="catalogLineDotShadow"
                        x="-50%"
                        y="-50%"
                        width="200%"
                        height="200%"
                      >
                        <feDropShadow
                          dx="0"
                          dy="1"
                          stdDeviation="1.5"
                          floodColor="#0f172a"
                          floodOpacity="0.25"
                        />
                      </filter>
                    </defs>
                    <CartesianGrid
                      stroke="#eaeaea"
                      strokeDasharray="4 4"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="name"
                      axisLine={false}
                      tickLine={false}
                      interval={0}
                      height={36}
                      tickMargin={4}
                      tick={
                        <AxisTick fullNameByShortName={fullNameByShortName} />
                      }
                    />
                    <YAxis
                      yAxisId="revenue"
                      orientation="left"
                      axisLine={false}
                      tickLine={false}
                      width={isMobile ? 46 : 60}
                      tick={{ fontSize: isMobile ? 10 : 12 }}
                      tickFormatter={value =>
                        value === 0 ? '(VND)' : formatCurrencyShort(value)
                      }
                    />
                    <YAxis
                      yAxisId="ticket"
                      orientation="right"
                      axisLine={false}
                      tickLine={false}
                      width={isMobile ? 46 : 60}
                      tick={{ fontSize: isMobile ? 10 : 12 }}
                      tickFormatter={value =>
                        value === 0
                          ? `(${translations?.['b2c.txt.ticket']})`
                          : formatCurrencyShort(value)
                      }
                    />
                    <Tooltip
                      content={
                        <ReportTooltip
                          translations={translations}
                          totalRevenue={totalRevenue}
                        />
                      }
                    />
                    <Bar
                      yAxisId="revenue"
                      dataKey="revenue"
                      fill={COLORS[0]}
                      radius={[4, 4, 0, 0]}
                      barSize={22}
                      name={translations?.['b2b.txt.revenueChart'] ?? ''}
                    >
                      {data.map((item, index) => (
                        <Cell
                          key={`bar-${item.name}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Bar>
                    <Line
                      yAxisId="ticket"
                      type="linear"
                      dataKey="ticketCount"
                      stroke={LINE_COLOR}
                      strokeWidth={2}
                      dot={{
                        r: 4,
                        fill: LINE_COLOR,
                        stroke: '#fff',
                        strokeWidth: 2,
                        filter: 'url(#catalogLineDotShadow)',
                      }}
                      activeDot={{
                        r: 6,
                        fill: LINE_COLOR,
                        stroke: '#fff',
                        strokeWidth: 2.5,
                        filter: 'url(#catalogLineDotShadow)',
                      }}
                      name={translations?.['b2b.txt.ticketsSold'] ?? ''}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </ChartScroll>
          )}
        </RenderIf>
      </LoadingContainer>
    </div>
  );
}

interface IPieLegendProps {
  data: { name: string; revenue: number }[];
}
const PieLegend = ({ data }: IPieLegendProps) => {
  const total = _.sumBy(data, 'revenue');
  return (
    <LegendWrapper>
      {data.map((item, index) => (
        <div key={item.name} className="legend-item">
          <span
            className="legend-dot"
            style={{ backgroundColor: COLORS[index % COLORS.length] }}
          />
          <span className="legend-label">
            {item.name} ({calcRevenuePercent(item.revenue, total)}%)
          </span>
        </div>
      ))}
    </LegendWrapper>
  );
};

const PieSection = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  .pie-chart-area {
    flex: 1 1 0;
    min-width: 0;
  }
  .pie-caption {
    text-align: center;
    font-size: 16px;
    font-weight: 600;
    margin-top: 8px;
  }
  @media (max-width: ${REPORT_MOBILE_WIDTH}px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    .pie-chart-area {
      width: 100%;
    }
    .pie-caption {
      font-size: 14px;
    }
  }
`;

const LegendWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  font-size: 16px;
  flex: 0 0 auto;
  .legend-item {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }
  .legend-dot {
    width: 16px;
    height: 16px;
    flex: 0 0 auto;
  }
  .legend-label {
    min-width: 0;
    overflow-wrap: anywhere;
  }
  @media (max-width: ${REPORT_MOBILE_WIDTH}px) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px 12px;
    font-size: 13px;
    .legend-dot {
      width: 12px;
      height: 12px;
    }
  }
  @media (max-width: 380px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const ChartTypeSwitch = styled(Switch)`
  background-color: #d9d9d9;
  &.ant-switch-checked {
    background: var(--color-gradient);
  }
`;

export default TicketTypeRevenueReport;
